/**
 * @fileoverview Shared checks of the Active Clients mock endpoints.
 * @description What every Active Clients mock handler needs before its own rules: the signed-in
 * Sales caller (403 / 2008 for anyone off the roster), the client a request names (404 / 4125 for a
 * bank that is not a client) and, for a command, the `Idempotency-Key` (428 / 4104), whether the
 * caller may take the action at all (403 / 2008) and that the client is not archived (409 / 4126,
 * except for Restore). Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { clientStatusOf, isClient } from './active-clients-mock-projection'
import { mockCaller } from './sales-crm-mock-projection'
import { failReply, headerOf } from './sales-crm-mock-response.utils'
import { findTenantById, listEveryTenant } from './tenants-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { ClientAction } from '@/features/sales/types/sales-client.types'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/** @description What every command needs before its own rules: the caller and the client. */
export interface CommandContext {
  caller: SalesUserRecord
  tenant: Tenant
}

/**
 * @description Every client, active and archived.
 * @returns {Tenant[]} The clients.
 */
export function listClients(): Tenant[] {
  return listEveryTenant().filter(isClient)
}

/**
 * @description The signed-in caller, or the 403 the backend answers for someone off the Sales roster.
 * @returns {SalesUserRecord | MockReply} The caller, or the refusal.
 */
export function callerOrRefusal(): SalesUserRecord | MockReply {
  return mockCaller() ?? failReply(403, '2008', 'Forbidden')
}

/**
 * @description Reads the client id out of a request's URL.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {number} [fromEnd] - Where the id sits, counted from the URL's end (-1 by default).
 * @returns {string} The id.
 */
export function clientIdOf(config: AxiosRequestConfig, fromEnd = -1): string {
  return decodeURIComponent(config.url?.split('/').at(fromEnd) ?? '')
}

/**
 * @description The client a request names, or the 404 the backend answers for one that does not exist
 * or whose bank is still a prospect.
 * @param {string} tenantId - The client's id.
 * @returns {Tenant | MockReply} The client, or the refusal.
 */
export function clientOrRefusal(tenantId: string): Tenant | MockReply {
  const tenant = findTenantById(tenantId)
  return tenant && isClient(tenant) ? tenant : failReply(404, '4125', 'Client not found')
}

/**
 * @description Runs the checks every command shares.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {number} idFromEnd - Where the client id sits in the URL, counted from its end.
 * @param {ClientAction} action - The action the command is.
 * @returns {CommandContext | MockReply} The caller and client, or the error to answer.
 */
export function checkCommand(
  config: AxiosRequestConfig,
  idFromEnd: number,
  action: ClientAction,
): CommandContext | MockReply {
  const caller = callerOrRefusal()
  if (Array.isArray(caller)) return caller
  if (!headerOf(config, 'Idempotency-Key')) {
    return failReply(428, '4104', 'Idempotency-Key is required')
  }
  const tenant = clientOrRefusal(clientIdOf(config, idFromEnd))
  if (Array.isArray(tenant)) return tenant
  const isSuperAdmin = caller.role === 'Super Admin'
  const isOwnerAction = action === 'INVITE_HR_ADMIN' || action === 'RESEND_HR_ADMIN_INVITE'
  const mayTry = isOwnerAction ? isSuperAdmin || tenant.currentOwnerId === caller.id : isSuperAdmin
  if (!mayTry) return failReply(403, '2008', 'Forbidden')
  if (clientStatusOf(tenant) === 'INACTIVE' && action !== 'RESTORE') {
    return failReply(409, '4126', 'The client is archived')
  }
  return { caller, tenant }
}
